import React from 'react';

import Loader from 'crehana-ui/Loader';

import userActivity from 'Utils/userActivity';
import {
  eventSources,
  eventNames,
  setFbEventToLocalStorage,
} from 'Jsx/Utils/facebookTracking/facebookTracking';
import buyProduct, { formatProduct } from 'Jsx/Utils/buyProduct';
import EVENTS from 'Jsx/Shared/Tracking/events';
import mixpanel from 'Jsx/Utils/mixpanelTracking';

import { useTranslation } from 'Shared/Hooks/useTranslation';
import { useMe } from 'Jsx/ContextApis/AuthContext';
import customLocalStorage from 'Jsx/Utils/customLocalStorage';

import { usePricesContext } from 'Jsx/CourseDetail/Context/Prices';

import { UserType } from 'Jsx/Types/UserType';
import { Course } from '../../types';
import EnrollmentButton from './EnrollmentButton';
import PurchaseButtons from './PurchaseButtons';

import useCanEnrollWithFirstEnroll from 'Jsx/CourseDetail/hooks/useCanEnrollWithFirstEnroll';
import CustomSignupForm from 'Jsx/Shared/CustomSignupForm';

interface TransactionButtonTypes {
  course: Course;
  user: UserType;
  isBuyable: boolean;
  canShowAccessPreview: boolean;
  canShowPaymentDialog?: boolean;
  canRenderAddToCartButton?: boolean;
  showPaymentDialog?: () => void;
  fromSticky?: boolean;
  customLabel?: string;
  addToCartTheme?: 'a' | 'b';
  renderCourseInfo?: JSX.Element;
}

const TransactionButtons: React.FC<TransactionButtonTypes> = ({
  course,
  course: {
    originalId: courseId,
    courseType,
    enrollmentUrl,
    urlToBuyNow,
    enrollmentAccessPreviewUrl,
  },
  canRenderAddToCartButton,
  renderCourseInfo,
  showPaymentDialog,
  fromSticky,
  customLabel,
  isBuyable,
  canShowAccessPreview,
  canShowPaymentDialog,
  addToCartTheme,
}) => {
  const { me: user, loading: loadingUserQuery } = useMe();
  const { canEnrollToCourseFreemium, source } = useCanEnrollWithFirstEnroll();
  const { loading: loadingPrices, prices } = usePricesContext();

  const userIsLogged = Boolean(user);
  const { t } = useTranslation();

  const buyCourse = () => {
    if (canShowPaymentDialog && showPaymentDialog) {
      showPaymentDialog();
    } else {
      const product = formatProduct(course, 'course');

      try {
        if (window.gtag) {
          window.gtag('event', 'Comprar ahora', {
            send_to: 'UA-60829200-1',
            event_category: 'PAYG',
            event_label: 'Abre checkout',
            value: course?.price,
          });
        }
        const fbTrackPayload = {
          content_name: course?.title,
          content_ids: courseId,
          content_type: 'course',
          value: course?.prices?.price,
          currency: course?.prices?.currencySymbol,
        };

        setFbEventToLocalStorage({
          eventName: eventNames.GO_TO_CHECKOUT,
          sourcePage: eventSources.COURSE_DETAIL,
          fbTrackPayload,
        });

        mixpanel.track(mixpanel.EVENTS.BUY, { product: 'course' }, () => {
          buyProduct(urlToBuyNow, product);
        });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const enrollInCourse = () => {
    const customData = {
      // eslint-disable-next-line no-restricted-globals
      item_path: location.pathname,
      item_id: course.originalId,
      item_name: course.title,
      item_type: 'course',
    };

    let mixpanelProps = {};

    if (canEnrollToCourseFreemium) {
      mixpanelProps = {
        isFreeTrial: true,
        source,
      };
    }

    mixpanel.track(EVENTS.COURSE.ENROLL, mixpanelProps);
    const _enrollmentUrl = canShowAccessPreview
      ? enrollmentAccessPreviewUrl
      : enrollmentUrl;

    if (userIsLogged) {
      if (user?.canEnrollFreemium) {
        customLocalStorage.setItem(
          'first_course_before_onboarding_sofe',
          'true',
        );
      }
      userActivity.track(userActivity.EVENTS.CLICK, customData, _enrollmentUrl);
    } else {
      if (canEnrollToCourseFreemium) {
        window.location.href = `/registro/?next=/registro-extra/?enrollmenturl=${enrollmentUrl}`;
        return;
      }
      window.nextUrl = _enrollmentUrl;
      if (typeof window.showRegisterModal === 'function') {
        window.showRegisterModal(
          _enrollmentUrl,
          undefined,
          undefined,
          'Course detail - Free course',
        );
      }
    }
  };

  if (loadingUserQuery || loadingPrices || !prices) {
    return (
      <div className="flex items-center justify-center w-full h-80">
        <Loader />
      </div>
    );
  }

  const userIsPremiumMember = user?.isPremiumMember;

  if (canEnrollToCourseFreemium) {
    return (
      <div className="p-24">
        <CustomSignupForm
          isCourseDetail
          registerButtonLabel={`${t('FREE_TRIAL')}*`}
          isRenderedIn="Course Detail"
          legalsCourse={t('VALID_ONLY')}
          courseEnrollmentUrl={enrollmentUrl}
        />
      </div>
    );
  }

  if (!isBuyable) {
    return (
      <React.Fragment>
        <EnrollmentButton
          courseType={courseType}
          customLabel={customLabel}
          canShowAccessPreview={canShowAccessPreview}
          enrollInCourse={enrollInCourse}
          userIsPremiumMember={userIsPremiumMember}
        />
        {renderCourseInfo}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <PurchaseButtons
        canRenderAddToCartButton={canRenderAddToCartButton}
        renderCourseInfo={renderCourseInfo}
        courseId={courseId}
        courseType={courseType}
        fromSticky={fromSticky}
        buyCourse={buyCourse}
        addToCartTheme={addToCartTheme}
      />
      {renderCourseInfo}
    </React.Fragment>
  );
};

export default TransactionButtons;
